import React, { useRef, useState } from "react";
import { allPolicies, policyCategories, searchPolicies } from "../data/policyLibrary";
import { buildPolicyPages, categoryLabel, PolicyDefinition } from "../data/policyTypes";
import { auditApi } from "../services/api";

const CUSTOM_KEY = "custom_policies";
const PAGE_LOG_KEY = "policy_page_log";
const READ_LOG_KEY = "policy_reading_log";

interface PageLogEntry {
  user: string;
  policyId: string;
  policyCode: string;
  page: number;
  pageTitle: string;
  seconds: number;
  at: string;
}

interface ReadLogEntry {
  id: string;
  policyId: string;
  policyCode: string;
  policyTitle: string;
  userName: string;
  completedAt: string;
  score: number;
}

interface Attachment {
  name: string;
  kind: string;
}

interface QuizOption {
  label: string;
  correct: boolean;
}

interface QuizQuestion {
  id: string;
  prompt: string;
  options: QuizOption[];
}

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);

    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (error) {
    return fallback;
  }
}

function useDictation(onText: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);

  const toggle = () => {
    const AnyWindow = window as any;
    const SR = AnyWindow.SpeechRecognition || AnyWindow.webkitSpeechRecognition;

    if (!SR) return;

    if (listening) {
      if (recRef.current) recRef.current.stop();
      setListening(false);
      return;
    }

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (event: any) => {
      let text = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) text += `${event.results[i][0].transcript} `;
      }

      if (text) onText(text);
    };
    rec.onend = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
  };

  return { listening, toggle };
}

export default function PolicyCentre({ user }: { user?: any }) {
  const userName = user && user.name ? user.name : "Unknown";

  const [view, setView] = useState<"library" | "reader" | "builder" | "monitor">("library");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [cqcFilter, setCqcFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [custom, setCustom] = useState<any[]>(() => loadJSON<any[]>(CUSTOM_KEY, []));
  const [selectedId, setSelectedId] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [quiz, setQuiz] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [quizDone, setQuizDone] = useState(false);
  const [score, setScore] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [readLog, setReadLog] = useState<ReadLogEntry[]>(() => loadJSON<ReadLogEntry[]>(READ_LOG_KEY, []));
  const [pageLog, setPageLog] = useState<PageLogEntry[]>(() => loadJSON<PageLogEntry[]>(PAGE_LOG_KEY, []));
  const [message, setMessage] = useState("");

  const [bTitle, setBTitle] = useState("");
  const [bKind, setBKind] = useState("Policy");
  const [bCategory, setBCategory] = useState("core");
  const [bCqc, setBCqc] = useState("Safe");
  const [bIntro, setBIntro] = useState("");
  const [bConclusion, setBConclusion] = useState("");
  const [bSections, setBSections] = useState<{ title: string; body: string }[]>([{ title: "", body: "" }]);
  const [bAttachments, setBAttachments] = useState<Attachment[]>([]);

  const policies = [...allPolicies, ...custom] as PolicyDefinition[];
  const selected = policies.find(p => p.id === selectedId);
  const pages = selected ? buildPolicyPages(selected) : [];
  const pageStartRef = useRef<number>(Date.now());
  const buildQuiz = (policy: PolicyDefinition): QuizQuestion[] => {
    const others = policies.filter(p => p.id !== policy.id);

    return policy.keyPoints.slice(0, 3).map((point, index) => {
      const distractors = others
        .slice(index * 3, index * 3 + 3)
        .map(p => p.keyPoints[0] || p.summary)
        .filter(label => label && label !== point)
        .slice(0, 3);

      const options: QuizOption[] = [
        { label: point, correct: true },
        ...distractors.map(label => ({ label, correct: false })),
      ].sort(() => Math.random() - 0.5);

      return { id: `q-${index}`, prompt: "Which statement comes from this document?", options };
    });
  };

  const logPageTime = (policy: PolicyDefinition, page: number, title: string) => {
    const seconds = Math.round((Date.now() - pageStartRef.current) / 1000);

    pageStartRef.current = Date.now();

    if (seconds < 1) return;

    const entry: PageLogEntry = {
      user: userName,
      policyId: policy.id,
      policyCode: policy.code,
      page: page + 1,
      pageTitle: title,
      seconds,
      at: new Date().toISOString(),
    };

    const next = [...pageLog, entry];

    setPageLog(next);
    localStorage.setItem(PAGE_LOG_KEY, JSON.stringify(next));
  };

  const openPolicy = (policy: PolicyDefinition) => {
    setSelectedId(policy.id);
    setPageIndex(0);
    pageStartRef.current = Date.now();
    setQuiz(buildQuiz(policy));
    setAnswers({});
    setQuizDone(false);
    setScore(0);
    setAgreed(false);
    setSignedName("");
    setMessage("");
    setView("reader");
  };

  const gotoPage = (index: number) => {
    if (selected) logPageTime(selected, pageIndex, pages[pageIndex].title);

    setPageIndex(index);
  };

  const closeReader = () => {
    if (selected) logPageTime(selected, pageIndex, pages[pageIndex].title);

    setSelectedId("");
    setView("library");
  };

  const submitQuiz = () => {
    let correct = 0;

    quiz.forEach(q => {
      const chosen = q.options.find(o => o.label === answers[q.id]);

      if (chosen && chosen.correct) correct += 1;
    });

    setScore(Math.round((correct / Math.max(1, quiz.length)) * 100));
    setQuizDone(true);
  };

  const signOff = () => {
    if (!selected) return;

    if (!agreed || !signedName) {
      setMessage("Tick the declaration and type your full name.");
      return;
    }

    const entry: ReadLogEntry = {
      id: `prl-${Date.now()}`,
      policyId: selected.id,
      policyCode: selected.code,
      policyTitle: selected.title,
      userName: signedName,
      completedAt: new Date().toISOString(),
      score,
    };

    const next = [entry, ...readLog];

    setReadLog(next);
    localStorage.setItem(READ_LOG_KEY, JSON.stringify(next));
    setMessage(`Declaration recorded for ${selected.code}. Thank you.`);
  };

  const attachFile = async (file: File) => {
    const kind = file.type.includes("pdf") ? "PDF" : file.name.endsWith(".xls") || file.name.endsWith(".xlsx") || file.type.includes("sheet") ? "Excel" : file.type.startsWith("image") ? "Image" : "Document";

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", kind);
      formData.append("context", "policy");
      await auditApi.uploadEvidence(formData);
      setBAttachments(current => [...current, { name: `${file.name} (uploaded)`, kind }]);
    } catch (error) {
      setBAttachments(current => [...current, { name: `${file.name} (attached)`, kind }]);
    }
  };

  const saveCustom = () => {
    if (!bTitle || !bIntro) {
      setMessage("Document name and introduction are required.");
      return;
    }

    const sections: { title: string; lines: string[] }[] = [
      { title: "1. Introduction", lines: bIntro.split("\n").filter(Boolean) },
    ];

    bSections.forEach((s, i) => {
      if (s.body.trim()) sections.push({ title: `${i + 2}. ${s.title || "Section"}`, lines: s.body.split("\n").filter(Boolean) });
    });

    sections.push({ title: "Conclusion", lines: (bConclusion || "This document is reviewed annually and after any significant change.").split("\n").filter(Boolean) });

    if (bAttachments.length > 0) {
      sections.push({ title: "Appendix - Attached documents", lines: bAttachments.map(a => `Attached ${a.kind}: ${a.name}`) });
    }

    const def: any = {
      id: `custom-${Date.now()}`,
      code: `CUS-${String(custom.length + 1).padStart(3, "0")}`,
      title: bTitle,
      category: bCategory,
      cqcFocus: bCqc,
      sop: bKind === "SOP",
      version: "1.0",
      reviewMonths: 12,
      summary: bIntro.slice(0, 180),
      keyPoints: bIntro.split("\n").filter(Boolean).slice(0, 3),
      relatedRegulations: [],
      sections,
      attachments: bAttachments,
    };

    const next = [...custom, def];

    setCustom(next);
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(next));
    setMessage(`${bKind} "${bTitle}" compiled as ${def.code} with the home logo and professional structure.`);
    setBTitle("");
    setBIntro("");
    setBConclusion("");
    setBSections([{ title: "", body: "" }]);
    setBAttachments([]);
    setView("library");
  };

  const introDict = useDictation(text => setBIntro(current => current + text));
  const conclusionDict = useDictation(text => setBConclusion(current => current + text));
  const monitorRows = (() => {
    const map: Record<string, { user: string; policyCode: string; seconds: number; pages: number[]; last: string }> = {};

    pageLog.forEach(entry => {
      const key = `${entry.user}|${entry.policyCode}`;

      if (!map[key]) map[key] = { user: entry.user, policyCode: entry.policyCode, seconds: 0, pages: [], last: entry.at };

      map[key].seconds += entry.seconds;

      if (!map[key].pages.includes(entry.page)) map[key].pages.push(entry.page);

      if (entry.at > map[key].last) map[key].last = entry.at;
    });

    return Object.values(map).sort((a, b) => (a.user < b.user ? -1 : 1));
  })();

  const list = policies.filter(policy => {
    if (category && policy.category !== category) return false;
    if (cqcFilter && policy.cqcFocus !== cqcFilter) return false;
    if (kindFilter === "Policy" && policy.sop) return false;
    if (kindFilter === "SOP" && !policy.sop) return false;
    if (query && !`${policy.code} ${policy.title} ${policy.summary}`.toLowerCase().includes(query.toLowerCase())) return false;

    return true;
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Policies and SOPs</h2>
          <p className="text-sm text-slate-600">Controlled documents aligned to the CQC single assessment framework, with reading time monitoring and a document builder.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setView("library")} className={view === "library" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Library</button>
          <button onClick={() => setView("builder")} className={view === "builder" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>New Policy or SOP</button>
          <button onClick={() => setView("monitor")} className={view === "monitor" ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>Reading Monitor</button>
        </div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {view === "library" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search policies and SOPs" className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <select value={category} onChange={event => setCategory(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="">All categories</option>
              {policyCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
            </select>
            <select value={cqcFilter} onChange={event => setCqcFilter(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="">All CQC key questions</option>
              <option>Safe</option>
              <option>Effective</option>
              <option>Caring</option>
              <option>Responsive</option>
              <option>Well led</option>
            </select>
            <select value={kindFilter} onChange={event => setKindFilter(event.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="">Policies and SOPs</option>
              <option value="Policy">Policies only</option>
              <option value="SOP">SOPs only</option>
            </select>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {list.map(policy => (
              <div key={policy.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-blue-900">{policy.code}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">{policy.sop ? "SOP" : "Policy"}</span>
                </div>
                <div className="mt-1 font-medium text-slate-900">{policy.title}</div>
                <div className="mt-1 text-xs text-slate-500">{categoryLabel(policy.category)} | CQC: {policy.cqcFocus} | Version {policy.version}</div>
                <button onClick={() => openPolicy(policy)} className="mt-3 rounded-lg bg-blue-900 px-4 py-1.5 text-sm font-medium text-white">Read</button>
              </div>
            ))}
            {list.length === 0 ? <div className="text-sm text-slate-500">No documents match your filters.</div> : null}
          </div>
        </div>
      ) : null}
      {view === "reader" && selected && pages.length > 0 ? (
        <div className="space-y-4">
          <button onClick={closeReader} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700">Back to library</button>

          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-[#123B36] pb-3">
              <div className="flex items-center gap-3">
                <img src="/logo512.png" alt="Roop Cottage logo" className="h-12 w-12 rounded-lg object-contain" />
                <div>
                  <div className="text-sm font-bold text-[#123B36]">Roop Cottage Residential Care Home</div>
                  <div className="text-[11px] text-slate-500">Wakefield Road, Fitzwilliam, Pontefract, WF9 5AN</div>
                </div>
              </div>
              <div className="text-right text-[11px] text-slate-500">
                <div>{selected.code} | Version {selected.version}</div>
                <div>{categoryLabel(selected.category)} | {selected.sop ? "Standard Operating Procedure" : "Policy"} | CQC: {selected.cqcFocus}</div>
              </div>
            </div>

            <div className="py-6">
              <h3 className="mb-3 text-lg font-semibold text-slate-900">{pages[pageIndex].title}</h3>
              <div className="space-y-2">
                {pages[pageIndex].lines.map((line, index) => (
                  <p key={index} className="text-sm leading-6 text-slate-700">{line}</p>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-[10px] text-slate-400">
              <div>Controlled document. Uncontrolled when printed.</div>
              <div>Page {pageIndex + 1} of {pages.length}</div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button disabled={pageIndex === 0} onClick={() => gotoPage(pageIndex - 1)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-40">Previous page</button>
            {pageIndex < pages.length - 1 ? (
              <button onClick={() => gotoPage(pageIndex + 1)} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Next page</button>
            ) : null}
          </div>

          {pageIndex === pages.length - 1 ? (
            <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold text-slate-900">Knowledge check</h3>
              {quiz.map(q => (
                <div key={q.id} className="rounded-lg border border-slate-100 p-3">
                  <div className="text-sm font-medium text-slate-800">{q.prompt}</div>
                  <div className="mt-2 space-y-1">
                    {q.options.map(option => (
                      <label key={option.label} className="flex items-start gap-2 text-sm text-slate-700">
                        <input type="radio" name={q.id} checked={answers[q.id] === option.label} onChange={() => setAnswers(current => ({ ...current, [q.id]: option.label }))} className="mt-0.5" />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {!quizDone ? (
                <button onClick={submitQuiz} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Submit answers</button>
              ) : (
                <div className={score >= 67 ? "rounded-lg bg-green-50 p-3 text-sm text-green-800" : "rounded-lg bg-red-50 p-3 text-sm text-red-800"}>
                  Your score is {score}%. {score >= 67 ? "You may now sign the declaration." : "Re-read the document and try again."}
                </div>
              )}
              {quizDone && score >= 67 ? (
                <div className="space-y-3 border-t border-slate-200 pt-3">
                  <label className="flex items-start gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={agreed} onChange={event => setAgreed(event.target.checked)} className="mt-0.5" />
                    I confirm that I have read and understood this document and I agree to work in line with it.
                  </label>
                  <input value={signedName} onChange={event => setSignedName(event.target.value)} placeholder="Type your full name as signature" className="w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                  <button onClick={signOff} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Sign declaration</button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {view === "builder" ? (
        <div className="max-w-3xl space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">New Policy or SOP</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-slate-600">Document name</label>
                <input value={bTitle} onChange={event => setBTitle(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Type</label>
                <select value={bKind} onChange={event => setBKind(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option>Policy</option>
                  <option>SOP</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Category</label>
                <select value={bCategory} onChange={event => setBCategory(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  {policyCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">CQC key question</label>
                <select value={bCqc} onChange={event => setBCqc(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option>Safe</option>
                  <option>Effective</option>
                  <option>Caring</option>
                  <option>Responsive</option>
                  <option>Well led</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Reference number</label>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">Auto: CUS-{String(custom.length + 1).padStart(3, "0")}</div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Introduction</label>
              <button onClick={introDict.toggle} className={introDict.listening ? "rounded-lg bg-red-600 px-3 py-1 text-xs text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-xs"}>
                {introDict.listening ? "Stop dictation" : "Voice to text"}
              </button>
            </div>
            <textarea value={bIntro} onChange={event => setBIntro(event.target.value)} rows={4} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          {bSections.map((section, index) => (
            <SectionEditor
              key={index}
              index={index}
              title={section.title}
              body={section.body}
              onTitle={value => setBSections(current => current.map((s, i) => (i === index ? { ...s, title: value } : s)))}
              onBody={value => setBSections(current => current.map((s, i) => (i === index ? { ...s, body: value } : s)))}
              onAppend={text => setBSections(current => current.map((s, i) => (i === index ? { ...s, body: s.body + text } : s)))}
              onRemove={() => setBSections(current => current.filter((_, i) => i !== index))}
            />
          ))}

          <button onClick={() => setBSections(current => [...current, { title: "", body: "" }])} className="rounded-lg border border-blue-900 px-4 py-2 text-sm font-medium text-blue-900">Add section</button>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Conclusion</label>
              <button onClick={conclusionDict.toggle} className={conclusionDict.listening ? "rounded-lg bg-red-600 px-3 py-1 text-xs text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-xs"}>
                {conclusionDict.listening ? "Stop dictation" : "Voice to text"}
              </button>
            </div>
            <textarea value={bConclusion} onChange={event => setBConclusion(event.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="mb-2 block text-xs font-medium text-slate-600">Attachments (PDF, Excel, pictures)</label>
            <input type="file" multiple onChange={event => { Array.from(event.target.files || []).forEach(file => attachFile(file)); }} className="w-full text-sm" />
            {bAttachments.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {bAttachments.map((a, i) => <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{a.kind}: {a.name}</span>)}
              </div>
            ) : null}
          </div>

          <button onClick={saveCustom} className="rounded-lg bg-green-700 px-5 py-2 text-sm font-medium text-white">Compile document with logo</button>
        </div>
      ) : null}
      {view === "monitor" ? (
        <div className="space-y-4">
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-xs">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-3 py-2 font-medium text-slate-700">Person</th>
                  <th className="px-3 py-2 font-medium text-slate-700">Document</th>
                  <th className="px-3 py-2 font-medium text-slate-700">Pages viewed</th>
                  <th className="px-3 py-2 font-medium text-slate-700">Time spent</th>
                  <th className="px-3 py-2 font-medium text-slate-700">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {monitorRows.map(row => (
                  <tr key={`${row.user}-${row.policyCode}`} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-800">{row.user}</td>
                    <td className="px-3 py-2">{row.policyCode}</td>
                    <td className="px-3 py-2">{row.pages.length}</td>
                    <td className="px-3 py-2">{Math.floor(row.seconds / 60)}m {row.seconds % 60}s</td>
                    <td className="px-3 py-2">{new Date(row.last).toLocaleString()}</td>
                  </tr>
                ))}
                {monitorRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-3 text-slate-500">No reading activity yet. Time on each page is recorded automatically while staff read documents.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 font-semibold text-slate-900">Signed declarations</h3>
            {readLog.map(entry => (
              <div key={entry.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2 text-xs">
                <div className="text-slate-800">{entry.userName} - {entry.policyCode} {entry.policyTitle}</div>
                <div className="text-slate-500">Score {entry.score}% | {new Date(entry.completedAt).toLocaleString()}</div>
              </div>
            ))}
            {readLog.length === 0 ? <div className="text-sm text-slate-500">No declarations signed yet.</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionEditor({ index, title, body, onTitle, onBody, onAppend, onRemove }: {
  index: number;
  title: string;
  body: string;
  onTitle: (value: string) => void;
  onBody: (value: string) => void;
  onAppend: (value: string) => void;
  onRemove: () => void;
}) {
  const dict = useDictation(onAppend);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-medium text-slate-600">Section {index + 2} title (optional)</label>
        <div className="flex gap-2">
          <button onClick={dict.toggle} className={dict.listening ? "rounded-lg bg-red-600 px-3 py-1 text-xs text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-xs"}>
            {dict.listening ? "Stop dictation" : "Voice to text"}
          </button>
          <button onClick={onRemove} className="text-xs text-red-600">Remove</button>
        </div>
      </div>
      <input value={title} onChange={event => onTitle(event.target.value)} placeholder="e.g. Procedure, Responsibilities, Monitoring" className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      <textarea value={body} onChange={event => onBody(event.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
    </div>
  );
}